import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Article } from 'src/app/services/article.service';
import {
  ReadTrackingBannerSource,
  ReadTrackingService
} from 'src/app/services/read-tracking.service';
import { SearchResult } from 'src/app/services/search.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'mvp-articles-list',
  templateUrl: './articles-list.component.html',
})
export class ArticlesListComponent implements OnInit {
  @ViewChild('list', { static: true }) list: ElementRef<HTMLUListElement>;

  @Input() articles: Array<Article | SearchResult>;
  @Input() toFullList: boolean = false;
  @Input() listSorting?: string;
  @Input() isVariantColor: boolean = false;
  @Input() title: string;
  @Input() trackingSource: 'homepage' | ReadTrackingBannerSource = 'homepage';

  assetUrl: string = environment.assetUrl;
  activeCardIndex: number = 0;
  environment = environment;

  constructor(
    private router: Router,
    private readTrackingService: ReadTrackingService
  ) {}

  ngOnInit() {
    this.updateActiveChip();
  }

  getThumbnailUrl(article: Article | SearchResult) {
    return `${this.assetUrl}/thumbnails/${article.thumbnail}`;
  }

  goToArticle(article: Article | SearchResult) {
    let queryParams: any = {};
    if (this.listSorting) {
      const sortDirection = this.listSorting.startsWith('-') ? 'desc' : 'asc';
      const sortColumn = this.listSorting.startsWith('-') ? this.listSorting.substring(1) : this.listSorting;
      queryParams.sort = sortColumn;
      queryParams.direction = sortDirection;
    }

    const trackedQueryParams = this.trackingSource === 'homepage'
      ? this.readTrackingService.withHomepageSource(queryParams)
      : this.readTrackingService.withBannerSource(this.trackingSource, queryParams);

    if (this.trackingSource !== 'homepage') {
      const trackingListUuids = this.serializeTrackingListUuids(this.articles);
      if (trackingListUuids) {
        trackedQueryParams.tracking_list_uuids = trackingListUuids;
      }
    }

    this.router.navigate([`article/${article.uuid}`], {
      queryParams: trackedQueryParams,
    });
  }

  private serializeTrackingListUuids(items: Array<Article | SearchResult>): string | undefined {
    const seen = new Set<string>();
    const orderedUuids = (items || [])
      .map(item => String(item?.uuid || '').trim())
      .filter(uuid => uuid !== '')
      .filter((uuid) => {
        if (seen.has(uuid)) {
          return false;
        }

        seen.add(uuid);
        return true;
      });

    if (orderedUuids.length < 2) {
      return undefined;
    }

    return orderedUuids.join(',');
  }

  goToList() {
    let queryParams: any = {};
    if (this.listSorting) queryParams.sort = this.listSorting;
    const newsHomeUrl = this.router.url.split('?')[0].startsWith('/news-bis') ? '/news-bis' : '/news';
    this.router.navigate([newsHomeUrl, 'list'], { queryParams });
  }

  scrollLeft() {
    this.list.nativeElement.scrollBy({ left: -300, behavior: 'smooth' });
  }

  scrollRight() {
    this.list.nativeElement.scrollBy({ left: 300, behavior: 'smooth' });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.updateActiveChip();
  }

  onScroll() {
    this.updateActiveChip();
  }

  updateActiveChip() {
    const listElement = this.list.nativeElement;
    const scrollLeft = listElement.scrollLeft;
    const viewportWidth = listElement.offsetWidth;

    let maxVisibleWidth = 0;
    let newActiveCardIndex = 0;

    const children = listElement.children;

    for (let i = 0; i < children.length; i++) {
      const card = children[i] as HTMLElement;
      const cardStart = card.offsetLeft - scrollLeft;
      const cardEnd = cardStart + card.offsetWidth;

      const visibleWidth = Math.min(cardEnd, viewportWidth) - Math.max(cardStart, 0);

      if (visibleWidth > maxVisibleWidth) {
        maxVisibleWidth = visibleWidth;
        newActiveCardIndex = i;
      }
    }

    this.activeCardIndex = newActiveCardIndex;
  }

  activeChipColor() {
    if (environment.application === 'tract') {
      return this.isVariantColor ? ' bg-gray-400 ' : ' bg-[#FFA500] ';
    }

    return ' bg-yellow-400';
  }

  inactiveChipColor() {
    if (environment.application === 'tract') {
      return this.isVariantColor ? ' bg-white ' : ' bg-gray-200 ';
    }   
    return ' bg-white';
  }
}
